import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useWizard } from "../../context/WizardContext";
import { bookLanguageDisplay } from "../../i18n/bookLanguageDisplay";
import { useTranslation } from "../../i18n/useTranslation";
import WizardLayout from "./WizardLayout";
import AdminPageNavigator from "../AdminPageNavigator";
import type { StoryStyleId } from "../../constants/storyStyles";
import { STORY_STYLE_IDS, storyStylePreviewUrl } from "../../constants/storyStyles";

const TOTAL_STEPS = 5;
const CURRENT_STEP = 2;

const STORY_TYPES = [
  {
    id: "surrogacy" as const,
    icon: "🌟",
    titleKey: "wizard.storyType.surrogacyName",
    descKey: "wizard.storyType.surrogacyDesc",
  },
  {
    id: "general" as const,
    icon: "📖",
    titleKey: "wizard.storyType.generalName",
    descKey: "wizard.storyType.generalDesc",
  },
];

function splitStoryStyleDescription(text: string): string[] {
  return text
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function StyleDescriptionStarIcon() {
  return (
    <svg
      className="mt-0.5 h-[14px] w-[14px] shrink-0 text-brandOrange"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.267 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.267-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const Step1b_StoryType: React.FC = () => {
  const {
    sessionId,
    language,
    storyType,
    setStoryType,
    storyStyle,
    setStoryStyle,
    updateStoryType,
    isLoading,
    error,
    clearError,
  } = useWizard();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState("");
  const [previewLoadError, setPreviewLoadError] = useState(false);
  const [previewLightboxOpen, setPreviewLightboxOpen] = useState(false);
  const illustrationStyleHeadingRef = useRef<HTMLHeadingElement>(null);
  const previewZoomTriggerRef = useRef<HTMLButtonElement>(null);
  const lightboxCloseRef = useRef<HTMLButtonElement>(null);
  const lightboxOpenedRef = useRef(false);

  const scrollToIllustrationStyle = () => {
    const el = illustrationStyleHeadingRef.current;
    if (!el) return;
    const reduceMotion =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  useEffect(() => {
    if (!sessionId) {
      navigate("/create/language");
    }
  }, [sessionId, navigate]);

  const previewSrc = useMemo(() => storyStylePreviewUrl(storyStyle), [storyStyle]);

  useEffect(() => {
    setPreviewLoadError(false);
  }, [previewSrc]);

  useEffect(() => {
    if (previewLightboxOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [previewLightboxOpen]);

  useEffect(() => {
    if (!previewLightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setPreviewLightboxOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [previewLightboxOpen]);

  useLayoutEffect(() => {
    if (previewLightboxOpen) {
      lightboxOpenedRef.current = true;
      requestAnimationFrame(() => lightboxCloseRef.current?.focus());
      return;
    }
    if (lightboxOpenedRef.current) {
      lightboxOpenedRef.current = false;
      window.setTimeout(() => previewZoomTriggerRef.current?.focus(), 0);
    }
  }, [previewLightboxOpen]);

  const previewTitleKey = `wizard.storyStyle.${storyStyle}Title` as const;
  const previewDescKey = `wizard.storyStyle.${storyStyle}Desc` as const;
  const styleDescriptionSegments = useMemo(
    () => splitStoryStyleDescription(t(previewDescKey)),
    [t, previewDescKey, storyStyle]
  );

  const handleNext = async () => {
    if (!storyType) {
      setValidationError(t("wizard.storyType.selectTypeFirst"));
      return;
    }
    setValidationError("");

    clearError();

    const success = await updateStoryType(storyType, storyStyle);

    if (success) {
      navigate("/create/photos-upload");
    }
  };

  const handleBack = () => {
    navigate("/create/language");
  };

  if (!sessionId) return null;

  return (
    <WizardLayout currentStep={CURRENT_STEP} totalSteps={TOTAL_STEPS} stageClassName="story-type-stage">
      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-10 flex w-full flex-1 flex-col items-center justify-center outline-none"
      >
        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 w-full max-w-md rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-red-700 shadow-soft"
          >
            <span className="block">{error}</span>
            <button
              type="button"
              onClick={clearError}
              className="mt-2 text-sm font-semibold text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
            >
              {t("wizard.common.dismissError")}
            </button>
          </div>
        )}

        {validationError && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 w-full max-w-lg rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-center text-amber-900 shadow-soft"
          >
            {validationError}
          </div>
        )}

        <div className="lang-card-root">
          <p className="lang-eyebrow">{t("wizard.step", { current: CURRENT_STEP, total: TOTAL_STEPS })}</p>
          <h1 className="lang-title">{t("wizard.storyType.title")}</h1>
          <AdminPageNavigator align="center" />

          <div className="flex justify-center">
            <div className="lang-badge-pill">
              🌐 {t("wizard.storyType.languageLabel")}: <strong>{bookLanguageDisplay(language, t)}</strong>
            </div>
          </div>

          <div className="story-grid" role="radiogroup" aria-label={t("wizard.storyType.title")}>
            {STORY_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                role="radio"
                aria-checked={storyType === type.id}
                className={`story-card${storyType === type.id ? " selected" : ""}`}
                onClick={() => {
                  setStoryType(type.id);
                  scrollToIllustrationStyle();
                }}
                disabled={isLoading}
              >
                <span className="lang-check" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 14 14" stroke="white" strokeWidth="2.8" fill="none">
                    <polyline points="2,7 6,11 12,3" />
                  </svg>
                </span>
                <span className="story-icon" aria-hidden="true">{type.icon}</span>
                <span className="story-body">
                  <span className="story-name">{t(type.titleKey)}</span>
                  <span className="story-desc">{t(type.descKey)}</span>
                </span>
              </button>
            ))}
          </div>

          <h2
            ref={illustrationStyleHeadingRef}
            className="mt-10 text-lg font-semibold text-[#40596B] text-center scroll-mt-4"
          >
            {t("wizard.storyStyle.sectionTitle")}
          </h2>
          <p className="text-center text-sm text-body mb-4 max-w-xl mx-auto">{t("wizard.storyStyle.sectionHint")}</p>

          <div className="style-wizard-shell mb-8 flex w-full min-w-0 flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mt-4">
            <div
              className="style-options-scroll mx-auto h-[500px] w-max max-w-full min-h-0 overflow-x-hidden rounded-xl border border-[#f0e6d8] bg-[#FFFAF4]/90 px-2 py-2 pr-1 lg:mx-0 lg:max-h-full"
              role="radiogroup"
              aria-label={t("wizard.storyStyle.sectionTitle")}
            >
              <ul className="h-full w-max space-y-1 overflow-y-auto overscroll-y-contain pe-1">
                {STORY_STYLE_IDS.map((id: StoryStyleId) => (
                  <li key={id} className="w-max max-w-full">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={storyStyle === id}
                      onClick={() => setStoryStyle(id)}
                      disabled={isLoading}
                      className={`w-max max-w-full text-left rounded-lg px-3 py-2.5 transition-colors flex gap-2 items-start border ${
                        storyStyle === id
                          ? "border-brandOrange bg-brandOrange/10 shadow-soft"
                          : "border-transparent hover:bg-white/80"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-4 w-4 shrink-0 rounded-full border-2 ${
                          storyStyle === id ? "border-brandOrange bg-brandOrange" : "border-[#cfd8e3]"
                        }`}
                        aria-hidden
                      />
                      <span>
                        <span className="block font-semibold text-[#40596B]">{t(`wizard.storyStyle.${id}Short`)}</span>
                        <span className="block text-xs text-body mt-0.5 whitespace-normal">{t(`wizard.storyStyle.${id}Title`)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="style-preview-pane flex h-[500px] w-full min-w-0 flex-1 flex-col rounded-xl border border-brandOrange/25 bg-brandOrange/10 p-4 shadow-soft lg:min-w-[280px]">
              {/* Fixed pixel height + absolute img so intrinsic image size never shifts layout */}
              <div className="relative mb-3 h-[220px] w-full shrink-0 overflow-hidden rounded-lg border border-black/5 bg-[#f5f5f5] sm:h-[240px]">
                {previewSrc && !previewLoadError ? (
                  <>
                    <img
                      src={previewSrc}
                      alt={t("wizard.storyStyle.previewAlt", { name: t(previewTitleKey) })}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                      onError={() => setPreviewLoadError(true)}
                    />
                    <button
                      ref={previewZoomTriggerRef}
                      type="button"
                      className="absolute end-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/95 text-[#40596B] shadow-soft transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
                      aria-label={t("wizard.storyStyle.enlargePreviewAria")}
                      onClick={() => setPreviewLightboxOpen(true)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.2-3.2" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center px-4">
                    <p className="text-center text-sm text-body">{t("wizard.storyStyle.missingPreviewBase")}</p>
                  </div>
                )}
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <h3 className="shrink-0 font-bold text-base text-heading">{t(previewTitleKey)}</h3>
                <div className="pe-1 overflow-y-auto">
                  <div className="flex flex-col gap-2 text-sm leading-relaxed text-body">
                    {styleDescriptionSegments.length > 0 ? (
                      styleDescriptionSegments.map((segment, i) => (
                        <div key={i} className="flex gap-2">
                          <StyleDescriptionStarIcon />
                          <span className="min-w-0">{segment}</span>
                        </div>
                      ))
                    ) : (
                      <p className="whitespace-pre-wrap">{t(previewDescKey)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading && (
            <div className="mb-4 rounded-lg bg-blue-100 p-3 text-center text-blue-800">
              {t("wizard.storyType.savingChoice")}
            </div>
          )}

          <div className="wizard-nav-row">
            <button type="button" className="wizard-btn-back" onClick={handleBack} disabled={isLoading}>
              ← {t("wizard.common.back")}
            </button>
            <button type="button" className="wizard-btn-next" onClick={handleNext} disabled={isLoading || !storyType}>
              {t("wizard.common.next")} →
            </button>
          </div>
        </div>
      </main>

      {previewLightboxOpen &&
        previewSrc &&
        !previewLoadError &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <button
              type="button"
              className="absolute inset-0 z-0 h-full w-full cursor-default border-0 bg-black/75 p-0 backdrop-blur-[2px]"
              aria-label={t("wizard.storyStyle.closePreviewLightboxAria")}
              onClick={() => setPreviewLightboxOpen(false)}
            />
            <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center p-4 pt-16">
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t("wizard.storyStyle.previewLightboxTitle", { name: t(previewTitleKey) })}
                className="pointer-events-auto flex max-h-[min(90vh,900px)] max-w-[min(95vw,1200px)] flex-col items-center"
              >
                <img
                  src={previewSrc}
                  alt={t("wizard.storyStyle.previewAlt", { name: t(previewTitleKey) })}
                  className="max-h-[min(85vh,860px)] max-w-full rounded-lg object-contain shadow-2xl"
                />
              </div>
            </div>
            <button
              ref={lightboxCloseRef}
              type="button"
              className="pointer-events-auto fixed top-4 end-4 z-[2] flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-white/15 text-white shadow-soft backdrop-blur-sm transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
              aria-label={t("wizard.storyStyle.closePreviewLightboxAria")}
              onClick={() => setPreviewLightboxOpen(false)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>,
          document.body
        )}
    </WizardLayout>
  );
};

export default Step1b_StoryType;
